/**
 * Chrome's install prompt (`beforeinstallprompt`, web.dev install criteria,
 * verified 2026-10-04): the event can fire before the start page renders, so
 * it is captured once at startup and handed to the install hint later.
 * Browsers without the event (Safari, Firefox) simply never fill it.
 */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  target.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export const installPrompt = (): InstallPromptEvent | null => deferred;

export function onInstallPromptChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Shows the browser's own install dialog once; `true` when the member accepted. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  deferred = null;
  notify();
  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
}

/** Already opened from the home screen (installed web app). */
export function runsStandalone(target: Window = window): boolean {
  const iosStandalone = (target.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || (target.matchMedia?.('(display-mode: standalone)').matches ?? false);
}

export const isIos = (target: Window = window): boolean => /iPhone|iPad|iPod/.test(target.navigator.userAgent);
